import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, X, Bot, Printer } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import JDGeneratorChatbot from './JDGeneratorChatbot';
import JobSkillsSection, { JobSkill } from './job/JobSkillsSection';
import PrintJDDialog from './job/PrintJDDialog';

interface JobFormProps {
  job?: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const CURRENCIES = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'INR', label: 'INR (₹)' },
  { value: 'AUD', label: 'AUD (A$)' },
  { value: 'CAD', label: 'CAD (C$)' },
];

const COUNTRIES = [
  { value: 'United States', label: 'United States' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Germany', label: 'Germany' },
  { value: 'France', label: 'France' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'India', label: 'India' },
  { value: 'Japan', label: 'Japan' },
  { value: 'Ireland', label: 'Ireland' },
  { value: 'Switzerland', label: 'Switzerland' },
  { value: 'Sweden', label: 'Sweden' },
  { value: 'Spain', label: 'Spain' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'Poland', label: 'Poland' },
  { value: 'UAE', label: 'United Arab Emirates' },
  { value: 'Israel', label: 'Israel' },
];

const JobForm = ({ job, open, onOpenChange, onSuccess }: JobFormProps) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [clientContacts, setClientContacts] = useState<any[]>([]);
  const [recruiters, setRecruiters] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [jobSkills, setJobSkills] = useState<JobSkill[]>([]);
  const [jobStatuses, setJobStatuses] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [showJDGenerator, setShowJDGenerator] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    location: '',
    country: '',
    city: '',
    job_type: 'full_time',
    work_mode: 'office',
    salary_min: '',
    salary_max: '',
    salary_currency: 'USD',
    client_id: '',
    status: 'draft',
    hide_client: false,
    hide_salary: false,
    assigned_to: '',
    secondary_recruiters: [] as string[],
    client_stakeholder_ids: [] as string[],
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchClients();
    fetchSkills();
    fetchRecruiters();
    fetchJobStatuses();
    fetchLocations();
    if (job) {
      setFormData({
        title: job.title || '',
        description: job.description || '',
        requirements: job.requirements || '',
        location: job.location || '',
        country: job.country || '',
        city: job.city || '',
        job_type: job.job_type || 'full_time',
        work_mode: job.work_mode || 'office',
        salary_min: job.salary_min || '',
        salary_max: job.salary_max || '',
        salary_currency: job.salary_currency || 'USD',
        client_id: job.client_id || '',
        status: job.status || 'draft',
        hide_client: job.hide_client || false,
        hide_salary: job.hide_salary || false,
        assigned_to: job.assigned_to || '',
        secondary_recruiters: job.secondary_recruiters || [],
        client_stakeholder_ids: job.client_stakeholder_ids || [],
      });
      fetchJobSkills(job.id);
      if (job.client_id) {
        fetchClientContacts(job.client_id);
      }
    } else {
      setJobSkills([]);
    }
  }, [job]);

  const fetchJobStatuses = async () => {
    const { data } = await supabase
      .from('master_job_statuses')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');
    setJobStatuses(data || []);
  };

  const fetchLocations = async () => {
    const { data } = await supabase
      .from('master_locations')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');
    setLocations(data || []);
  };

  useEffect(() => {
    if (formData.client_id) {
      fetchClientContacts(formData.client_id);
    } else {
      setClientContacts([]);
      setFormData(prev => ({ ...prev, client_stakeholder_ids: [] }));
    }
  }, [formData.client_id]);

  const fetchClientContacts = async (clientId: string) => {
    const { data } = await supabase
      .from('client_contacts')
      .select('*')
      .eq('client_id', clientId)
      .order('is_primary', { ascending: false });
    setClientContacts(data || []);
  };

  const fetchRecruiters = async () => {
    const { data } = await supabase
      .from('app_users')
      .select('id, full_name, email, role')
      .eq('is_active', true)
      .in('role', ['recruiter', 'admin']);
    setRecruiters(data || []);
  };

  const fetchClients = async () => {
    const { data } = await supabase.from('clients').select('*').eq('is_active', true);
    setClients(data || []);
  };

  const fetchSkills = async () => {
    const { data } = await supabase.from('skills').select('*').order('name');
    setSkills(data || []);
  };

  const fetchJobSkills = async (jobId: string) => {
    const { data } = await supabase
      .from('job_skills')
      .select('skill_id, skill_category, years_experience, skills(name, type)')
      .eq('job_id', jobId);
    
    if (data) {
      const mappedSkills: JobSkill[] = data.map((js: any) => ({
        skillId: js.skill_id,
        skillName: js.skills?.name || 'Unknown',
        skillType: js.skills?.type || 'technical',
        category: js.skill_category || 'required_technical',
        yearsExperience: js.years_experience || undefined
      }));
      setJobSkills(mappedSkills);
    }
  };

  const handleApplyGeneratedContent = async (content: {
    description: string;
    requirements: string;
    skills: {
      technical: { name: string; experience: string | null; required: boolean; id: string | null; matched: boolean }[];
      soft: { name: string; experience: string | null; required: boolean; id: string | null; matched: boolean }[];
    };
  }) => {
    setFormData(prev => ({
      ...prev,
      description: content.description,
      requirements: content.requirements
    }));

    const newJobSkills: JobSkill[] = [];

    // Process technical skills
    for (const skill of content.skills.technical) {
      let skillId = skill.id;
      
      if (!skill.matched) {
        try {
          const { data, error } = await supabase
            .from('skills')
            .insert([{ name: skill.name, type: 'technical' }])
            .select()
            .single();

          if (!error && data) {
            setSkills(prev => [...prev, data]);
            skillId = data.id;
          }
        } catch (e) {
          console.error('Error adding skill:', skill.name, e);
          continue;
        }
      }

      if (skillId) {
        const yearsMatch = skill.experience?.match(/(\d+)/);
        newJobSkills.push({
          skillId,
          skillName: skill.name,
          skillType: 'technical',
          category: skill.required ? 'required_technical' : 'good_to_have_technical',
          yearsExperience: yearsMatch ? parseInt(yearsMatch[1]) : undefined
        });
      }
    }

    // Process soft skills
    for (const skill of content.skills.soft) {
      let skillId = skill.id;
      
      if (!skill.matched) {
        try {
          const { data, error } = await supabase
            .from('skills')
            .insert([{ name: skill.name, type: 'soft' }])
            .select()
            .single();

          if (!error && data) {
            setSkills(prev => [...prev, data]);
            skillId = data.id;
          }
        } catch (e) {
          console.error('Error adding skill:', skill.name, e);
          continue;
        }
      }

      if (skillId) {
        newJobSkills.push({
          skillId,
          skillName: skill.name,
          skillType: 'soft',
          category: 'soft',
          yearsExperience: undefined
        });
      }
    }

    setJobSkills(prev => {
      const existingIds = prev.map(s => s.skillId);
      const uniqueNew = newJobSkills.filter(s => !existingIds.includes(s.skillId));
      return [...prev, ...uniqueNew];
    });
    
    toast({
      title: 'Content applied',
      description: `Added description, requirements, and ${newJobSkills.length} skills`
    });
  };

  const toggleSecondaryRecruiter = (recruiterId: string) => {
    setFormData(prev => ({
      ...prev,
      secondary_recruiters: prev.secondary_recruiters.includes(recruiterId)
        ? prev.secondary_recruiters.filter(id => id !== recruiterId)
        : [...prev.secondary_recruiters, recruiterId]
    }));
  };

  const toggleStakeholder = (contactId: string) => {
    setFormData(prev => ({
      ...prev,
      client_stakeholder_ids: prev.client_stakeholder_ids.includes(contactId)
        ? prev.client_stakeholder_ids.filter(id => id !== contactId)
        : [...prev.client_stakeholder_ids, contactId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent, overrideStatus?: string) => {
    e.preventDefault();
    setLoading(true);

    const statusToUse = overrideStatus || formData.status;

    try {
      const jobData: any = {
        title: formData.title,
        description: formData.description,
        requirements: formData.requirements,
        location: formData.location,
        country: formData.country || null,
        city: formData.city || null,
        job_type: formData.job_type,
        work_mode: formData.work_mode,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        salary_currency: formData.salary_currency,
        client_id: formData.client_id || null,
        status: statusToUse,
        hide_client: formData.hide_client,
        hide_salary: formData.hide_salary,
        assigned_to: formData.assigned_to || null,
        secondary_recruiters: formData.secondary_recruiters,
        client_stakeholder_ids: formData.client_stakeholder_ids,
        published_at: statusToUse === 'published' || statusToUse === 'open' ? new Date().toISOString() : (job?.published_at || null)
      };

      let jobId = job?.id;

      if (job) {
        const { error } = await supabase.from('jobs').update(jobData).eq('id', job.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('jobs').insert([jobData]).select().single();
        if (error) throw error;
        jobId = data.id;
      }

      // Update job skills with new structure
      await supabase.from('job_skills').delete().eq('job_id', jobId);
      if (jobSkills.length > 0) {
        const jobSkillsData = jobSkills.map(skill => ({
          job_id: jobId,
          skill_id: skill.skillId,
          skill_category: skill.category,
          years_experience: skill.yearsExperience || null
        }));
        await supabase.from('job_skills').insert(jobSkillsData);
      }

      toast({ title: job ? 'Job updated successfully' : 'Job created successfully' });
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getClientName = () => {
    return clients.find(c => c.id === formData.client_id)?.name || '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{job ? 'Edit Job' : 'Create New Job'}</DialogTitle>
          <DialogDescription>Fill in the details below to {job ? 'update' : 'create'} a job posting</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="title">Job Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="client">Client</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 pt-6">
                <Switch
                  id="hide_client"
                  checked={formData.hide_client}
                  onCheckedChange={(checked) => setFormData({ ...formData, hide_client: checked })}
                />
                <Label htmlFor="hide_client" className="cursor-pointer">Hide client in public view</Label>
              </div>

              {formData.client_id && (
                <div className="col-span-2">
                  <Label>Client Stakeholders</Label>
                  <div className="border rounded-md p-3 max-h-32 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {clientContacts.map(contact => (
                        <Badge
                          key={contact.id}
                          variant={formData.client_stakeholder_ids.includes(contact.id) ? "default" : "outline"}
                          className="cursor-pointer hover:opacity-80"
                          onClick={() => toggleStakeholder(contact.id)}
                        >
                          {contact.name}
                          {contact.position && <span className="ml-1 opacity-70">({contact.position})</span>}
                          {contact.is_primary && <span className="ml-1 text-xs">★</span>}
                          {formData.client_stakeholder_ids.includes(contact.id) && (
                            <X className="ml-1 h-3 w-3" />
                          )}
                        </Badge>
                      ))}
                    </div>
                    {clientContacts.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        No contacts found for this client
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {jobStatuses.map(status => (
                      <SelectItem key={status.id} value={status.code}>
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: status.color }}
                          />
                          {status.display_name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Recruiter Assignment */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Recruiter Assignment</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="assigned_to">Primary Recruiter</Label>
                <Select
                  value={formData.assigned_to}
                  onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select primary recruiter" />
                  </SelectTrigger>
                  <SelectContent>
                    {recruiters.map(recruiter => (
                      <SelectItem key={recruiter.id} value={recruiter.id}>
                        {recruiter.full_name} ({recruiter.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Secondary Recruiters</Label>
                <div className="border rounded-md p-3 max-h-32 overflow-y-auto">
                  <div className="flex flex-wrap gap-2">
                    {recruiters
                      .filter(r => r.id !== formData.assigned_to)
                      .map(recruiter => (
                        <Badge
                          key={recruiter.id}
                          variant={formData.secondary_recruiters.includes(recruiter.id) ? "default" : "outline"}
                          className="cursor-pointer hover:opacity-80"
                          onClick={() => toggleSecondaryRecruiter(recruiter.id)}
                        >
                          {recruiter.full_name}
                          {formData.secondary_recruiters.includes(recruiter.id) && (
                            <X className="ml-1 h-3 w-3" />
                          )}
                        </Badge>
                      ))}
                  </div>
                  {recruiters.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      No recruiters available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Job Details</h3>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPrintDialog(true)}
                  disabled={!formData.title}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Print JD
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => setShowJDGenerator(true)}
                >
                  <Bot className="mr-2 h-4 w-4" />
                  AI JD Generator
                </Button>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                className="bg-background"
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    [{ 'indent': '-1' }, { 'indent': '+1' }],
                    ['link'],
                    ['clean']
                  ],
                }}
              />
            </div>

            <div>
              <Label>Requirements</Label>
              <ReactQuill
                theme="snow"
                value={formData.requirements}
                onChange={(value) => setFormData({ ...formData, requirements: value })}
                className="bg-background"
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    [{ 'indent': '-1' }, { 'indent': '+1' }],
                    ['link'],
                    ['clean']
                  ],
                }}
              />
            </div>
          </div>

          {/* Skills Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Skills</h3>
            <JobSkillsSection
              skills={skills}
              jobSkills={jobSkills}
              onSkillsChange={setJobSkills}
              onSkillsRefresh={fetchSkills}
            />
          </div>

          {/* Work Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Work Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="job_type">Job Type</Label>
                <Select
                  value={formData.job_type}
                  onValueChange={(value) => setFormData({ ...formData, job_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="freelance">Freelance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="work_mode">Work Mode</Label>
                <Select
                  value={formData.work_mode}
                  onValueChange={(value) => setFormData({ ...formData, work_mode: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">Work from Office</SelectItem>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => {
                    const newCity = formData.city;
                    const newLocation = newCity ? `${newCity}, ${value}` : value;
                    setFormData({ ...formData, country: value, location: newLocation });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    {COUNTRIES.map(country => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => {
                    const newCity = e.target.value;
                    const newLocation = formData.country 
                      ? (newCity ? `${newCity}, ${formData.country}` : formData.country)
                      : newCity;
                    setFormData({ ...formData, city: newCity, location: newLocation });
                  }}
                  placeholder="Enter city"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="location">Location (Display Text)</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Auto-generated from City & Country, or enter custom text"
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-generated from City & Country. You can edit this manually if needed.
                </p>
              </div>
            </div>
          </div>

          {/* Compensation */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Compensation</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="salary_currency">Currency</Label>
                <Select
                  value={formData.salary_currency}
                  onValueChange={(value) => setFormData({ ...formData, salary_currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(curr => (
                      <SelectItem key={curr.value} value={curr.value}>{curr.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 pt-6">
                <Switch
                  id="hide_salary"
                  checked={formData.hide_salary}
                  onCheckedChange={(checked) => setFormData({ ...formData, hide_salary: checked })}
                />
                <Label htmlFor="hide_salary" className="cursor-pointer">Hide salary in public view</Label>
              </div>

              <div>
                <Label htmlFor="salary_min">Minimum Salary</Label>
                <Input
                  id="salary_min"
                  type="number"
                  value={formData.salary_min}
                  onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                  disabled={formData.hide_salary}
                />
              </div>

              <div>
                <Label htmlFor="salary_max">Maximum Salary</Label>
                <Input
                  id="salary_max"
                  type="number"
                  value={formData.salary_max}
                  onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                  disabled={formData.hide_salary}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between gap-3">
            <div className="flex gap-2">
              {job && formData.status === 'draft' && (
                <Button 
                  type="button" 
                  variant="default"
                  onClick={(e) => handleSubmit(e, 'open')}
                  disabled={loading}
                >
                  Publish Job
                </Button>
              )}
              {job && formData.status === 'open' && (
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={(e) => handleSubmit(e, 'draft')}
                  disabled={loading}
                >
                  Unpublish
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {job ? 'Update Job' : 'Create Job'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>

      {/* JD Generator Chatbot */}
      <JDGeneratorChatbot
        open={showJDGenerator}
        onOpenChange={setShowJDGenerator}
        jobContext={{
          title: formData.title,
          clientName: getClientName(),
          jobType: formData.job_type,
          workMode: formData.work_mode,
          location: formData.location,
          country: formData.country,
          city: formData.city,
          salaryMin: formData.salary_min,
          salaryMax: formData.salary_max,
          salaryCurrency: formData.salary_currency,
          existingSkills: jobSkills.map(s => s.skillName)
        }}
        onApply={handleApplyGeneratedContent}
      />

      {/* Print JD Dialog */}
      <PrintJDDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        job={formData}
        clientName={getClientName()}
        skills={{
          required_technical: jobSkills
            .filter(s => s.category === 'required_technical')
            .map(s => ({ id: s.skillId, name: s.skillName, years_experience: s.yearsExperience })),
          good_to_have_technical: jobSkills
            .filter(s => s.category === 'good_to_have_technical')
            .map(s => ({ id: s.skillId, name: s.skillName })),
          soft: jobSkills
            .filter(s => s.category === 'soft')
            .map(s => ({ id: s.skillId, name: s.skillName })),
        }}
      />
    </Dialog>
  );
};

export default JobForm;
